import { Icon } from '../ui/Icon';
import styles from './Connections.module.css';

export type ConnectionStarter = 'brain' | 'stripe' | 'agent' | 'custom';
export const connectionStarters = {
  brain: {
    name: 'My Second Brain',
    protocol: 'mcp' as const,
    url: '',
    instructions:
      'Consult this system for prior business decisions when working on my goals. Link the sources it returns.',
    title: 'Your Second Brain',
    description:
      'Bring your own notes and decisions into the conversation through the abilities your system exposes.',
    needs:
      'You’ll need a reachable MCP endpoint or A2A Agent Card from your brain system. A local vault or Telegram bot needs to expose one first.',
    icon: 'brain',
  },
  stripe: {
    name: 'Stripe',
    protocol: 'mcp' as const,
    url: 'https://mcp.stripe.com',
    instructions:
      'Prepare invoices using accepted proposals and my invoicing policy. I will review external writes before they run.',
    title: 'Stripe',
    description:
      'Give Echo access to selected customer and invoice tools, with review before external writes.',
    needs:
      'Have a restricted or agent API key authorized for Stripe’s MCP endpoint ready. This setup currently uses a key rather than browser sign-in. Confirm your account and test environment before approving invoice work.',
    icon: 'credit-card',
  },
  agent: {
    name: '',
    protocol: 'a2a' as const,
    url: '',
    instructions:
      'Use this agent for research relevant to my requests. Share only the context it needs, and bring its findings and sources back to this conversation.',
    title: 'Another AI agent',
    description:
      'Ask a compatible agent to investigate a question and return findings, progress, or a request for input.',
    needs:
      'You’ll need the agent’s A2A 0.3.0 Agent Card URL and an access token if it requires one. Ask the agent’s provider or administrator for these.',
    icon: 'message-circle',
  },
  custom: {
    name: '',
    protocol: 'mcp' as const,
    url: '',
    instructions: '',
    title: 'Another tool or service',
    description:
      'Connect a compatible MCP server for your own tools, data, or business systems.',
    needs:
      'You’ll need the service’s public HTTPS MCP endpoint and an access token if required. Ask its provider or administrator for connection details.',
    icon: 'plug',
  },
};
export function ConnectionsIntroduction({
  onStart,
}: {
  onStart: (starter: ConnectionStarter) => void;
}) {
  return (
    <section
      className={styles.introduction}
      aria-labelledby="connections-introduction"
    >
      <div className={styles.introductionCopy}>
        <span className={styles.eyebrow}>
          Tools, knowledge, and other agents
        </span>
        <h2 id="connections-introduction">Give Echo more to work with.</h2>
        <p>
          Connect tools, knowledge sources, and other agents through MCP or A2A.
          Choose what Echo can access and explain when to use it. Then ask for
          help in your normal chat.
        </p>
        <div className={styles.protocolExplanation}>
          <div>
            <div className={styles.protocolHeading}>
              <ProtocolMark protocol="mcp" />
              <strong>MCP connects tools and knowledge</strong>
            </div>
            <p>
              Model Context Protocol lets Echo use a service’s selected
              abilities: search notes, retrieve records, or prepare a reviewed
              action.
            </p>
          </div>
          <div>
            <div className={styles.protocolHeading}>
              <ProtocolMark protocol="a2a" />
              <strong>A2A connects another agent</strong>
            </div>
            <p>
              Agent-to-Agent lets Echo hand over a task, follow its progress,
              answer questions, and bring the findings back.
            </p>
          </div>
        </div>
        <p className={styles.introductionFootnote}>
          One system can offer both. You choose its abilities and permissions;
          its name alone grants no access. Connections can also support Auto
          Progress when you have separately enabled it.
        </p>
      </div>
      <section
        className={styles.startSection}
        aria-labelledby="connection-start-title"
      >
        <div>
          <h2 id="connection-start-title">Connect a system</h2>
          <p className={styles.muted}>
            Choose a starting point for your MCP or A2A connection.
          </p>
        </div>
        <div className={styles.starterGrid}>
          {(Object.keys(connectionStarters) as ConnectionStarter[]).map(
            (id) => {
              const starter = connectionStarters[id];
              return (
                <button
                  key={id}
                  type="button"
                  className={styles.starterCard}
                  onClick={() => onStart(id)}
                >
                  <Icon name={starter.icon} size={24} />
                  <strong>{starter.title}</strong>
                  <span>{starter.description}</span>
                  <span className={styles.starterAction}>
                    {id === 'stripe'
                      ? 'Connect Stripe'
                      : id === 'brain'
                        ? 'Connect your brain'
                        : id === 'agent'
                          ? 'Connect an agent'
                          : 'Connect a service'}
                    <Icon name="arrow-right" size={16} />
                  </span>
                </button>
              );
            },
          )}
        </div>
      </section>
    </section>
  );
}

function ProtocolMark({ protocol }: { protocol: 'mcp' | 'a2a' }) {
  return <span className={`${styles.protocolMark} ${protocol === 'mcp' ? styles.mcpMark : styles.a2aMark}`} aria-hidden="true" />;
}
